import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { uniqBy } from 'lodash';

import {
  Ryecon,
  RyeconDatabase,
  RyeconDot,
  RyeconList,
  RyeconListTree,
} from '@dbt-labs/sourdough';

import { useAdminDevStagingProdEnvs } from '@/hooks/admin/environments';
import { Project, useProjectsLazy } from '@/hooks/admin/project';
import { useValidatedParams } from '@/hooks/params';
import { useQueryParamOption } from '@/hooks/queryParamOption';
import { useEnvironmentSwitcher } from '@/hooks/useEnvironmentSwitcher';
import { MockDbtCloudProject } from '@/MockDbtCloudProject';
import { UrlEnvironmentType } from '@/typings/url';
import { useDevelopmentContext } from '../DevelopmentContextProvider';
import { useFeatureFlag } from '../FeatureFlagProvider';
import {
  DBT_ASSETS_ROOT_NODE_NAME,
  DBT_DATABASE_ROOT_NODE_NAME,
  DBT_FILES_ROOT_NODE_NAME,
  PROJECT_PAGE_SIZE,
} from './const';
import { TreeData, ViewType } from './types';
import { useDbtAssetsTree } from './useDbtAssetsTree';
import { useDbtDatabaseTree } from './useDbtDatabaseTree';
import { useDbtFileTree } from './useDbtFileTree';

const VIEW_ROOT_NODE_NAME: Record<ViewType, string> = {
  assetList: DBT_ASSETS_ROOT_NODE_NAME,
  fileTree: DBT_FILES_ROOT_NODE_NAME,
  databaseView: DBT_DATABASE_ROOT_NODE_NAME,
};

const ENVIRONMENT_RYECON: Record<UrlEnvironmentType, Ryecon> = {
  production: ((props: any) => (
    <RyeconDot {...props} className="text-2xl text-fgInfo" />
  )) as Ryecon,
  staging: ((props: any) => (
    <RyeconDot {...props} className="text-2xl text-bgDuotoneSuccess" />
  )) as Ryecon,
  development: ((props: any) => (
    <RyeconDot {...props} className="text-2xl text-bgBrand" />
  )) as Ryecon,
};

const FILE_TREE_VIEW_RYECON = {
  assetList: RyeconList,
  fileTree: RyeconListTree,
  databaseView: RyeconDatabase,
};

const ENVIRONMENT_SHORT_NAME_MAP: Record<UrlEnvironmentType, string> = {
  production: 'Prod',
  staging: 'Stg',
  development: 'Dev',
};

export const useDbtTree = (): TreeData => {
  const { environmentType, accountId, projectId } = useValidatedParams();

  const hasDatabaseViewFeature = useFeatureFlag('meta5066DatabaseView', false);

  const [viewParam, setViewParam] = useQueryParamOption({
    param: 'view',
    options: ['assetList', 'fileTree', 'databaseView'],
    validateOption: (value: string | null) => {
      if (!value) return 'assetList';
      if (value === 'databaseView' && !hasDatabaseViewFeature) return 'assetList';
      return value as ViewType;
    },
  });

  const hasDevEnvFeature = useFeatureFlag(
    'meta3092ExplorerEnableDevEnvironment',
    false,
  );

  const fileTreeView = viewParam || 'assetList';
  const [selectedEnvironmentType, setSelectedEnvironmentType] =
    useState<UrlEnvironmentType>(
      (environmentType ?? 'production') as UrlEnvironmentType,
    );

  const [allProjects, setAllProjects] = useState<Project[]>([]);

  // Store initial projects separately for environment dropdown check
  const [initialProjects, setInitialProjects] = useState<Project[]>([]);

  // Check if any project in the account has a staging environment
  // Check both initialProjects (from initial load) and allProjects (lazily loaded projects)
  const hasStagingEnvironment = useMemo(() => {
    const checkProjects = (projects: Project[]) =>
      projects.some((project) =>
        project.environments?.some(
          (env) => env.deployment_type === 'staging' || env.type === 'staging',
        ),
      );

    return checkProjects(initialProjects) || checkProjects(allProjects);
  }, [initialProjects, allProjects]);
  const [, getProjects] = useProjectsLazy();
  const location = useLocation();
  const navigate = useNavigate();

  // Get admin environment IDs for the current project
  const adminEnvironments = useAdminDevStagingProdEnvs({
    accountId: accountId ?? 0,
    projectId: projectId ?? 0,
  }).data;

  const productionEnv = adminEnvironments?.production?.id;
  const stagingEnv = adminEnvironments?.staging?.id;
  const devEnv = adminEnvironments?.development?.id;

  // Get environment URLs with preserved paths using useEnvironmentSwitcher
  const { environmentUrls } = useEnvironmentSwitcher(productionEnv, stagingEnv, devEnv);

  const [projectsPageInfo, setProjectsPageInfo] = useState<{
    hasNextPage: boolean;
    totalCount: number;
    after: Record<string, string>;
  }>({
    hasNextPage: false,
    totalCount: 0,
    after: {},
  });

  const devData = useDevelopmentContext();
  useEffect(() => {
    getProjects({
      accountId: accountId ?? 0,
      first: PROJECT_PAGE_SIZE,
      after: '',
      includeRelated: ['environments'],
    }).then((res) => {
      let data: Project[] = res.data;

      // at time of writing, if we don't have access token during development, we need to mock the projects based on what we find in projects.ts
      if (res?.error && devData.isDev) {
        const accountProjects = devData.mockProjects.filter(
          (p) => p?.accountId === (accountId ?? 0) || p.projectId === projectId,
        );
        data = accountProjects.map((p: MockDbtCloudProject) => ({
          account_id: accountId ?? 0,
          id: p.projectId,
          name: p.name,
          environments: p.environments ?? [
            ...(p.productionEnvironmentId
              ? [
                  {
                    id: p.productionEnvironmentId,
                    deployment_type: 'production',
                    name: 'Production',
                  },
                ]
              : []),
            ...(p.stagingEnvironmentId
              ? [
                  {
                    id: p.stagingEnvironmentId,
                    deployment_type: 'staging',
                    name: 'Staging',
                  },
                ]
              : []),
          ],
        }));
      }

      // Store initial projects for environment dropdown check (after potential mock transformation)
      if (data && data.length > 0) {
        setInitialProjects(data);
      }

      if (!data || data.length === 0) return;
      setAllProjects(data as Project[]);
    });
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dbtFileTree = useDbtFileTree({
    setAllProjects,
    allProjects,
    projectsPageInfo,
    setProjectsPageInfo,
    viewParam: fileTreeView,
    selectedEnvironmentType,
  });

  const dbtAssetsTree = useDbtAssetsTree({
    setAllProjects,
    allProjects,
    projectsPageInfo,
    setProjectsPageInfo,
    viewParam: fileTreeView,
    selectedEnvironmentType,
  });

  const dbtDatabaseTree = useDbtDatabaseTree({
    setAllProjects,
    allProjects,
    projectsPageInfo,
    setProjectsPageInfo,
    viewParam: fileTreeView,
    selectedEnvironmentType,
  });

  const currentTree = useMemo(() => {
    if (fileTreeView === 'databaseView') return dbtDatabaseTree;
    if (fileTreeView === 'assetList') return dbtAssetsTree;
    return dbtFileTree;
  }, [fileTreeView, dbtAssetsTree, dbtFileTree, dbtDatabaseTree]);

  // Auto-expand the root node when switching views (skip initial mount)
  const previousViewRef = useRef(fileTreeView);
  useEffect(() => {
    if (previousViewRef.current === fileTreeView) return;
    previousViewRef.current = fileTreeView;

    const rootNodeName = VIEW_ROOT_NODE_NAME[fileTreeView];
    currentTree.setOpenDirectories(
      (prev) => (prev?.includes(rootNodeName) ? prev : [rootNodeName, ...(prev || [])]),
      {} as React.MouseEvent<HTMLButtonElement, MouseEvent>,
    );
    currentTree.generateChildrenForPath(rootNodeName);
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileTreeView]);

  // Track the root node's object identity so this effect re-runs when the
  // tree is rebuilt by child hooks (new node objects) but does NOT loop when
  // the effect itself mutates the existing node in-place.
  const rootNode = currentTree.fileTree.find((node) => node.parent === 'root');

  // update the root node buttons
  useEffect(() => {
    currentTree.setFileTree((prev) => {
      const updatedNode = prev.find((node) => node.parent === 'root');
      if (!updatedNode) return prev;
      const actions = [
        {
          ryecon: FILE_TREE_VIEW_RYECON[fileTreeView],
          items: [
            {
              label: 'Asset list',
              value: 'assetList',
              ryecon: FILE_TREE_VIEW_RYECON.assetList,
            },
            {
              label: 'File tree',
              value: 'fileTree',
              ryecon: FILE_TREE_VIEW_RYECON.fileTree,
            },
            ...(hasDatabaseViewFeature
              ? [
                  {
                    label: 'Database view',
                    value: 'databaseView',
                    ryecon: FILE_TREE_VIEW_RYECON.databaseView,
                  },
                ]
              : []),
          ],
          name: 'file-tree-view-change',
          onMenuItemClick: (item: string) => {
            setViewParam(item as ViewType);
          },
          menuButtonProps: {
            shouldPortal: true,
          },
        },
        {
          text: ENVIRONMENT_SHORT_NAME_MAP[selectedEnvironmentType],
          iconSize: 'lg',
          ryecon:
            ENVIRONMENT_RYECON[
              selectedEnvironmentType as keyof typeof ENVIRONMENT_RYECON
            ],
          items: [
            {
              value: 'production',
              label: 'Production',
              ryecon: ENVIRONMENT_RYECON.production,
              iconSize: 'lg',
            },
            ...(hasStagingEnvironment
              ? [
                  {
                    value: 'staging',
                    label: 'Staging',
                    ryecon: ENVIRONMENT_RYECON.staging,
                    iconSize: 'lg',
                  },
                ]
              : []),
            ...(hasDevEnvFeature
              ? [
                  {
                    value: 'development',
                    label: 'Development',
                    ryecon: ENVIRONMENT_RYECON.development,
                    iconSize: 'lg',
                  },
                ]
              : []),
          ],
          name: 'file-tree-view-change',
          onMenuItemClick: (item: string) => {
            const newEnvironmentType = item as UrlEnvironmentType;
            setSelectedEnvironmentType(newEnvironmentType);

            // Only navigate if we're currently on an environment-scoped page
            // For non-environment pages (like catalog), just update the selected state
            // environmentType will be undefined on non-environment pages
            if (environmentType) {
              const targetUrl = environmentUrls[newEnvironmentType];
              if (targetUrl) {
                navigate(targetUrl);
              }
            }
          },
          menuButtonProps: {
            shouldPortal: true,
          },
        },
      ];

      updatedNode.data.actions = actions;
      updatedNode.data.actionsAlwaysVisible = true;
      return uniqBy([...prev, updatedNode], 'id');
    });
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fileTreeView,
    selectedEnvironmentType,
    hasStagingEnvironment,
    hasDevEnvFeature,
    hasDatabaseViewFeature,
    location.pathname,
    environmentUrls,
    navigate,
    rootNode,
  ]);

  return currentTree;
};
